// @ts-strict-ignore
import type {
  AggregationComponent,
  AggregationFilter,
  AggregationFilterTimeframeValue,
} from 'flow/store/engine/types';
import { AggregationFilterOperator } from './types';
import type { AggregationSourceData } from './types';
import { INITIAL_AGGREGATION_CONFIG } from './constants';

const TIMEFRAME_COLUMN_SLUG = 'created_at';

const SCOPE_TO_COLUMN_SLUG: Record<string, string> = {
  entity: 'entity_token',
  group: 'group_token',
};

const SCOPE_COLUMN_SLUGS = new Set(
  Object.values(SCOPE_TO_COLUMN_SLUG),
);

const COLUMN_SLUG_TO_SCOPE = Object.fromEntries(
  Object.entries(SCOPE_TO_COLUMN_SLUG).map(([k, v]) => [v, k]),
);

function buildFiltersForPersistence(
  sourceData: AggregationSourceData,
): AggregationFilter[] {
  const filters = [...sourceData.filters];

  const columnSlug = SCOPE_TO_COLUMN_SLUG[sourceData.scope];
  if (columnSlug) {
    filters.push({
      columnSlug,
      dynamicColumnSlug: columnSlug,
      operatorSlug: AggregationFilterOperator.EQ,
      staticValue: null,
    });
  }

  filters.push({
    columnSlug: TIMEFRAME_COLUMN_SLUG,
    dynamicColumnSlug: TIMEFRAME_COLUMN_SLUG,
    operatorSlug: AggregationFilterOperator.BT,
    staticValue: {
      unit: sourceData.timeframeUnit,
      interval: [sourceData.timeframeIntervalStart, sourceData.timeframeIntervalEnd],
    },
  });

  return filters;
}

/**
 * Scope is not stored as a discrete field — it's inferred from the
 * presence of a scope filter (entity_token, group_token) whose
 * static_value is resolved by Typhon at evaluation time.
 */
function deriveScopeFromFilters(
  scopeFilter: AggregationFilter | undefined,
): string {
  if (!scopeFilter) return 'all';
  return COLUMN_SLUG_TO_SCOPE[scopeFilter.columnSlug] ?? 'all';
}

function splitFilters(filters: AggregationFilter[]): {
  scopeFilter?: AggregationFilter;
  timeframeFilter?: AggregationFilter;
  nonScopeFilters: AggregationFilter[];
} {
  let scopeFilter: AggregationFilter | undefined;
  let timeframeFilter: AggregationFilter | undefined;
  const nonScopeFilters: AggregationFilter[] = [];

  for (const f of filters ?? []) {
    if (SCOPE_COLUMN_SLUGS.has(f.columnSlug)) {
      scopeFilter = f;
    } else if (f.columnSlug === TIMEFRAME_COLUMN_SLUG) {
      timeframeFilter = f;
    } else {
      nonScopeFilters.push(f);
    }
  }

  return { scopeFilter, timeframeFilter, nonScopeFilters };
}

/**
 * Merges aggregation editor sourceData (form state) back into the existing
 * persisted AggregationComponent[], preserving fields the editor doesn't
 * manage (objectSlug, objectVersionPublicVersion).
 */
export function sourceDataToAggregationComponents(
  sourceData: AggregationSourceData,
  existingComponents: AggregationComponent[],
): AggregationComponent[] {
  return [{
    ...existingComponents[0],
    displayName: sourceData.name,
    displayDescription: sourceData.description,
    aggregation: sourceData.aggregation,
    filters: buildFiltersForPersistence(sourceData),
  }];
}

/**
 * Reverse of sourceDataToAggregationComponents: hydrates the editor form
 * from previously saved AggregationComponent[]. Scope and timeframe are
 * extracted from system filters; the remainder are attribute filters from
 * config objects. Missing fields fall back to INITIAL_AGGREGATION_CONFIG.
 */
export function aggregationComponentsToSourceData(
  components: AggregationComponent[],
): AggregationSourceData {
  if (!components?.length) {
    return INITIAL_AGGREGATION_CONFIG;
  }
  const component = components[0];
  const { scopeFilter, timeframeFilter, nonScopeFilters } = splitFilters(
    component.filters,
  );

  const timeframe = timeframeFilter?.staticValue as AggregationFilterTimeframeValue | undefined;

  return {
    ...INITIAL_AGGREGATION_CONFIG,
    name: component.displayName,
    description: component.displayDescription,
    aggregation: component.aggregation,
    scope: deriveScopeFromFilters(scopeFilter),
    ...(timeframe && {
      timeframeUnit: timeframe.unit,
      timeframeIntervalStart: timeframe.interval[0],
      timeframeIntervalEnd: timeframe.interval[1],
    }),
    filters: nonScopeFilters,
  };
}
